import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './multiRangeSlider.module.css';

type Props = {
  min: number;
  max: number;
  onChange: ({ min, max }: { min: number; max: number }) => void;
};

const MultiRangeSlider = ({ min, max, onChange }: Props) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);

  const range = useRef<HTMLDivElement>(null);
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);

  // Convert to percentage
  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  // Set width of the range to decrease from the left side
  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(Number(maxValRef.current.value));

      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(Number(minValRef.current.value));
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);

  // Get min and max values when their state changes
  useEffect(() => {
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal]);

  return (
    <div className={styles.multiRangeContainer}>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={minVal}
        ref={minValRef}
        onChange={(event) => {
          const value = Math.min(Number(event.target.value), maxVal - 1);
          setMinVal(value);
          event.target.value = value.toString();
        }}
        className={clsx(styles.thumb, styles.thumbZindex3, {
          [styles.thumbZindex5]: minVal > max - 100,
        })}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={maxVal}
        ref={maxValRef}
        onChange={(event) => {
          const value = Math.max(Number(event.target.value), minVal + 1);
          setMaxVal(value);
          event.target.value = value.toString();
        }}
        className={clsx(styles.thumb, styles.thumbZindex4)}
      />
      <div className={styles.slider}>
        <div className={styles.sliderTrack} />
        <div ref={range} className={styles.sliderRange} />
        <div className={styles.sliderLeftValue}>{minVal + ' km'}</div>
        <div className={styles.sliderRightValue}>{maxVal + ' km'}</div>
      </div>
    </div>
  );
};

export default MultiRangeSlider;
